import React, { useState, useMemo, useCallback } from 'react'
import { Input, Table, Tag, Descriptions } from 'antd'
import { useTranslation } from 'react-i18next'

const ExpandedQuestRow = ({ record }) => {
  if (!record || !record.stops || record.stops.length === 0) return <div style={{ padding: 16 }}>No details available</div>

  return (
    <div style={{ padding: '16px', background: '#fafafa', borderRadius: '8px', margin: '0 16px' }}>
      <h4 style={{ marginBottom: 16, color: '#1890ff' }}>{record.groupName} - Progress Details</h4>
      {record.stops.map(stop => (
        <div key={stop.stop_number} style={{ marginBottom: 16, borderLeft: '3px solid #1890ff', paddingLeft: 12 }}>
          <h5 style={{ marginBottom: 8, fontSize: '14px' }}>Stop {stop.stop_number} <Tag color="blue" style={{ marginLeft: 8 }}>{stop.points_earned} Pts</Tag></h5>
          <Descriptions size="small" column={1} bordered style={{ background: '#fff' }}>
            {stop.mc && (
              <Descriptions.Item label="Multiple Choice">
                <span style={{ marginRight: 16 }}>Selected: {stop.mc.selected_index}</span>
                <Tag color={stop.mc.is_correct ? 'green' : 'red'}>
                  {stop.mc.is_correct ? 'Correct' : 'Incorrect'}
                </Tag>
              </Descriptions.Item>
            )}
            {stop.open_ended && (
              <Descriptions.Item label="Open Ended">
                <div style={{ fontStyle: 'italic', color: '#555', marginBottom: 4 }}>"{stop.open_ended.answer}"</div>
                {stop.open_ended.feedback && <div style={{ marginBottom: 4 }}>Feedback: {stop.open_ended.feedback}</div>}
                <Tag color={stop.open_ended.is_correct ? 'green' : 'red'}>
                  {stop.open_ended.is_correct ? 'Correct' : 'Incorrect'}
                </Tag>
              </Descriptions.Item>
            )}
            {stop.video && (
              <Descriptions.Item label="Video">
                <span style={{ marginRight: 16 }}>Status: {stop.video.processing ? 'Processing' : 'Complete'}</span>
                {stop.video.feedback && <div>Feedback: {stop.video.feedback}</div>}
              </Descriptions.Item>
            )}
            {!stop.mc && !stop.open_ended && !stop.video && (
              <Descriptions.Item label="Details">No specific interactions recorded.</Descriptions.Item>
            )}
          </Descriptions>
        </div>
      ))}
    </div>
  )
}

const QuestLeaderboardTable = ({
  loading = false,
  leaderboardData = {},
  selectedCompetition
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const limit = 10
  const { t } = useTranslation()

  const data = useMemo(() => {
    return (leaderboardData && leaderboardData.leaderboard) || []
  }, [leaderboardData])

  const tableData = useMemo(() => {
    return data.map((item, index) => {
      return {
        key: item.groupId || `quest-${index}`,
        rank: item.rank || index + 1,
        groupName: item.groupName || 'Unknown Group',
        points: item.points || 0,
        completedStops: item.completedStops || 0,
        completed: item.completed || false,
        groupId: item.groupId,
        stops: item.stops || []
      }
    })
  }, [data])

  const filteredData = useMemo(() => {
    return tableData.filter(group => {
      return group.groupName.toLowerCase().includes(searchTerm.toLowerCase())
    })
  }, [tableData, searchTerm])

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * limit
    const endIndex = startIndex + limit
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, page, limit])

  const paginationConfig = useMemo(
    () => ({
      current: page,
      pageSize: limit,
      total: filteredData.length,
      showSizeChanger: false,
      showQuickJumper: false,
      showTotal: (total, range) =>
        `${range[0]}-${range[1]} ${t('general:of')} ${total} ${t('v3:items')}`,
      onChange: newPage => setPage(newPage)
    }),
    [page, limit, filteredData.length, t]
  )

  const getRankSuffix = useCallback(rank => {
    if (rank <= 3) {
      return rank === 1 ? 'st' : rank === 2 ? 'nd' : 'rd'
    }
    return 'th'
  }, [])

  const columns = useMemo(() => {
    return [
      {
        title: t('v4:rank') || 'Rank',
        dataIndex: 'rank',
        key: 'rank',
        width: 100,
        render: rank => (
          <div className="rank-cell">
            <span className="rank-number">{rank}</span>
            <span className="rank-suffix">{getRankSuffix(rank)}</span>
          </div>
        )
      },
      {
        title: 'Group Name',
        dataIndex: 'groupName',
        key: 'groupName',
        width: 250,
        render: name => <span className="user-name" style={{ fontWeight: '600' }}>{name}</span>
      },
      {
        title: 'Points',
        dataIndex: 'points',
        key: 'points',
        width: 120,
        render: points => <span className="points-cell">{points}</span>
      },
      {
        title: 'Completed Stops',
        dataIndex: 'completedStops',
        key: 'completedStops',
        width: 150
      },
      {
        title: 'Status',
        dataIndex: 'completed',
        key: 'completed',
        width: 120,
        render: completed => (
          <span style={{ color: completed ? '#52c41a' : '#faad14', fontWeight: '500' }}>
            {completed ? 'Completed' : 'In Progress'}
          </span>
        )
      }
    ]
  }, [getRankSuffix, t])

  return (
    <div className="quest-leaderboard-table-section leaderboard-table-section">
      <h3 className="leaderboard-table-title">{t('v4:leaderboard')}</h3>
      <div className="leaderboard-controls">
        <div className="search-controls" style={{ marginLeft: 'auto' }}>
          <Input.Search
            allowClear
            placeholder="Search by group name"
            className="leaderboard-search-input"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <div className="table-container">
        <Table
          loading={loading}
          columns={columns}
          dataSource={paginatedData}
          pagination={paginationConfig}
          locale={{
            emptyText: (
              <div className="no-data-message">
                {t('v4:no_leaderboard_data_available')}
              </div>
            )
          }}
          className="leaderboard-ant-table"
          expandedRowRender={record => <ExpandedQuestRow record={record} />}
          expandRowByClick
        />
      </div>
    </div>
  )
}

export default QuestLeaderboardTable
